/* eslint-disable @typescript-eslint/ban-ts-comment */
import { GoogleAnalytics } from '@next/third-parties/google'
import { SpeedInsights } from '@vercel/speed-insights/next'
import type { AppContext, AppProps } from 'next/app'
import { Inter } from 'next/font/google'
import Head from 'next/head'
// @ts-ignore
import { PagesTopLoader } from 'nextjs-toploader/pages'
import { NuqsAdapter } from 'nuqs/adapters/next/pages'

import '@/styles/globals.css' // Adjust this path to your global styles
import 'react-markdown-editor-lite/lib/index.css'
import '@/styles/markdown.css'
import '@/styles/puck-editor.css'
import '@/styles/toc.css' // Table of Contents styles
import '@puckeditor/core/puck.css'

import { fetchTenantConfigServer } from '@/lib/fetch-tenant-config-server'
import { resolveTenantSubdomainFromHostname } from '@/lib/resolve-tenant-subdomain'
import { getTenantMediaDefaults } from '@/lib/tenant-media-defaults'

import AppPageRenderer from '@/components/common/app-page-renderer'
import CommunityNotFoundPage from '@/components/common/community-not-found-page'
import LoadingSpinner from '@/components/common/loading-spinner'
import StructuredData from '@/components/seo/StructuredData'
import { TenantFaviconHead } from '@/components/tenant/tenant-favicon-head'
import { TenantCustomBody, TenantCustomCSS, TenantCustomHead, TenantTheme } from '@/components/tenant/tenant-theme'
import { ToasterSonner } from '@/components/ui/sonner'
import { ToastProvider } from '@/components/ui/toast'
import { Toaster } from '@/components/ui/toaster'

import { buildWebsiteSchema, resolveRuntimeBrandingMetadata } from '@/configs/schema-metadata'
import Providers from '@/contexts/providers'
import { communityConfigService } from '@/services/community-config.service'
import { TenantConfig } from '@/services/tenant.service'
import {
  getTenantFromRequest,
  getTenantFromWindow,
  isOnFullstackdatascienceDomainFromRequest,
  usesFsdsPlatformTenantConfig,
} from '@/utils/tenant.utils'

const inter = Inter({ subsets: ['latin'] })

interface CustomAppProps extends AppProps {
  tenantId?: string
  tenantConfig?: TenantConfig | null
  initialHostname?: string
  communityNotFound?: boolean
}

function MyApp({
  Component,
  pageProps,
  tenantId,
  tenantConfig,
  initialHostname,
  communityNotFound,
}: CustomAppProps) {
  if (communityNotFound) {
    return (
      <main className={`${inter.className}`}>
        <Head>
          <meta charSet="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <CommunityNotFoundPage subdomain={tenantId} />
      </main>
    )
  }

  const hostname = initialHostname || (typeof window !== 'undefined' ? window.location.hostname : '')
  const isFsdsDomain = isOnFullstackdatascienceDomainFromRequest(hostname)
  const loaderColor = isFsdsDomain ? '#f04438' : '#3B82F6'
  const tenantFaviconUrl = getTenantMediaDefaults(tenantConfig?.customization).defaultFaviconUrl ?? ''
  const brandingMetadata = resolveRuntimeBrandingMetadata({
    hostname,
    tenantName: tenantConfig?.name,
    tenantLogo: tenantConfig?.theme?.logo,
    tenantFaviconUrl: tenantFaviconUrl ?? '',
  })
  const websiteSchema = buildWebsiteSchema({
    hostname,
    tenantName: tenantConfig?.name,
    tenantLogo: tenantConfig?.theme?.logo,
    tenantFaviconUrl: tenantFaviconUrl ?? '',
  })

  return (
    <main className={`${inter.className}`}>
      <Head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        {/* <!-- HTML Meta Tags --> */}
        {/* <!-- googlebot bot --> */}
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="google" content="sitelinkssearchbox" />
        <meta name="author" content={brandingMetadata.authorName} />
        <meta name="application-name" content={brandingMetadata.appTitle} />
        <meta name="apple-mobile-web-app-title" content={brandingMetadata.appTitle} />
      </Head>
      <GoogleAnalytics gaId={process.env.NEXT_PUBLIC_MEASUREMENT_ID || ''} />
      <style jsx global>{`
        html {
          font-family: ${inter.style.fontFamily};
        }
      `}</style>
      <Providers initialTenantId={tenantId} initialTenantConfig={tenantConfig} initialHostname={initialHostname}>
        <TenantFaviconHead />
        <TenantCustomHead />
        <TenantCustomCSS />
        <NuqsAdapter>
          <TenantTheme />
          <ToastProvider>
            <ToasterSonner />
            <PagesTopLoader
              color={loaderColor}
              shadow={`0 0 10px ${loaderColor},0 0 5px ${loaderColor}`}
              zIndex={1500}
              showSpinner={false}
            />
            <LoadingSpinner color={loaderColor} />
            <AppPageRenderer Component={Component} pageProps={pageProps} />
            <TenantCustomBody />
            <Toaster />
          </ToastProvider>
        </NuqsAdapter>
      </Providers>
      <SpeedInsights />
      <StructuredData schema={websiteSchema} />
    </main>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const { ctx } = appContext
  let tenantId: string | undefined
  let tenantConfig: TenantConfig | null | undefined
  let initialHostname = ''
  let communityNotFound = false

  // Extract tenant ID from request (works in both SSR and CSR)
  if (ctx.req) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const req = ctx.req as any
    initialHostname = req.headers?.host || ''
    const hostname = initialHostname.split(':')[0]
    tenantId = getTenantFromRequest({ headers: req.headers, cookies: req.cookies })

    if (!tenantId || tenantId === 'default') {
      tenantId = await resolveTenantSubdomainFromHostname(hostname)
    }

    if (tenantId && tenantId !== 'default') {
      const result = await fetchTenantConfigServer(req, ctx.res)
      tenantConfig = result.config
      communityNotFound = result.communityNotFound
      if (result.tenantId) {
        tenantId = result.tenantId
      }
    }

    if (communityNotFound && ctx.res) {
      ctx.res.statusCode = 404
    }
  } else if (typeof window !== 'undefined') {
    initialHostname = window.location.host
    const hostname = window.location.hostname
    tenantId = getTenantFromWindow()

    if (!tenantId || tenantId === 'default') {
      tenantId = await resolveTenantSubdomainFromHostname(hostname)
    }

    if (tenantId && tenantId !== 'default' && !usesFsdsPlatformTenantConfig(hostname)) {
      try {
        const community = await communityConfigService.getBySubdomain(tenantId)
        if (!community) {
          communityNotFound = true
        }
      } catch {
        // Transient failure — do not block client navigations.
      }
    }
  }

  return {
    tenantId,
    tenantConfig,
    initialHostname,
    communityNotFound,
  }
}

export default MyApp
